import { createContext, useContext, useEffect, useState, useReducer, useRef } from './h.js'
import { setLanguage, detectLanguage, t } from './i18n.js'
import { tones } from './tones.js'
import { CALL_STATE } from '@pine/core'

export const AppContext = createContext(null)

export function useApp () {
  return useContext(AppContext)
}

/**
 * Everything mutable that the UI cares about, in one place.
 *
 * PineClient is the source of truth; this only mirrors what the UI needs to
 * re-render on, so there is never a second copy of a message to keep in sync.
 */
export function createStore (client, { calls = null, platform = {} } = {}) {
  const listeners = new Set()

  // Transient things a back gesture should close before anything routed —
  // an open emoji panel, say. They are held here rather than in `state`
  // because closing one is the component's own business; the store only needs
  // to know, in order, who gets first refusal on the gesture.
  const backHandlers = []

  const state = {
    route: 'chats',        // chats | contacts | settings
    roomId: null,
    panel: false,          // info panel open
    mobileView: 'list',    // list | chat | panel
    search: '',
    filter: 'all',
    status: 'connecting',
    mode: 'unknown',
    peers: 0,
    incomingCall: null,
    activeCall: null,
    speaker: false,       // loudspeaker rather than the earpiece
    audioRoutes: null,    // { current, wanted, available, name } from the shell
    callMinimized: false, // the call shrunk to a floating card
    lightbox: null,
    dialog: null,
    replyTo: null,
    editing: null,
    storyViewer: null      // { order: [author], author, index } while a story is open
  }

  const store = {
    client,
    calls,
    platform,
    state,

    get (key) { return state[key] },

    set (patch) {
      let changed = false
      for (const [key, value] of Object.entries(patch)) {
        if (state[key] !== value) {
          state[key] = value
          changed = true
        }
      }
      if (changed) store.emit()
    },

    emit () {
      for (const fn of listeners) fn(state)
    },

    subscribe (fn) {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },

    async openRoom (roomId) {
      store.set({ roomId, mobileView: 'chat', replyTo: null, editing: null, route: 'chats' })
      if (!roomId) return null

      // A chat restored by name only has no key yet, so there is no room to
      // open — selecting it shows what is known about it and the button that
      // asks the members for the rest.
      const known = client.getSummary(roomId)
      if (known && known.placeholder) return null

      const room = await client.openRoom(roomId)
      room.markRead().catch(() => {})
      // Somebody looking at a chat wants what is in it now: if the others
      // are not here, wake them so their phones hand it over.
      client.pullMissed(roomId)
      return room
    },

    closeRoom () {
      store.set({ roomId: null, mobileView: 'list', panel: false })
    },

    showDialog (dialog) { store.set({ dialog }) },
    hideDialog () { store.set({ dialog: null }) },

    /** The speakerphone button. Only Android can act on it; see platform(). */
    setSpeaker (on) {
      store.setAudioRoute(on ? 'speaker' : 'earpiece')
    },

    /**
     * Send the call to a particular output — or back to 'auto', which follows
     * whatever headset is connected.
     */
    setAudioRoute (route) {
      const audio = platform.callAudio
      if (audio && audio.available) {
        if (audio.setRoute) audio.setRoute(route)
        else audio.setSpeaker(route === 'speaker')
        if (audio.routes) store.set({ audioRoutes: audio.routes() })
      }
      store.set({ speaker: route === 'speaker' })
    },

    /** Whether to offer that button at all. */
    get canRouteAudio () {
      return !!(platform.callAudio && platform.callAudio.available)
    },

    /**
     * Shrink the call to a card and give the app back.
     *
     * The call itself is untouched - the surface stays mounted and only its
     * layout changes - because the remote audio plays through the <video>
     * elements on it. Unmounting them to get the screen back would have
     * silenced the person you were talking to.
     */
    minimizeCall () {
      if (!state.activeCall) return
      /*
       * A call answered on a locked phone is shown over the lock screen —
       * and so is everything behind it. Shrinking the call would hand the
       * chat list to whoever is holding the phone, so while it is locked
       * the phone is asked to unlock first, and the call only shrinks once
       * it has been (see 'pine:unlocked' below).
       */
      const lock = platform.lockScreen
      if (lock && lock.locked()) {
        lock.unlock()
        return
      }
      store.set({ callMinimized: true })
    },
    restoreCall () { store.set({ callMinimized: false }) },

    /**
     * Let a component claim the back gesture while something transient is
     * open. Returns the function that unregisters it again.
     */
    pushBack (handler) {
      backHandlers.push(handler)
      return () => {
        const at = backHandlers.indexOf(handler)
        if (at !== -1) backHandlers.splice(at, 1)
      }
    },

    /**
     * Handle a system back gesture. Returns true when it consumed one, so the
     * Android shell knows whether to close the activity — otherwise back from
     * inside a chat would quit the app instead of returning to the list.
     */
    /** Whether back would close something rather than leave the app. */
    canGoBack () {
      return backHandlers.length > 0 || !!state.storyViewer || !!state.lightbox || !!state.dialog ||
        !!(state.activeCall && !state.callMinimized) || !!state.panel || !!state.roomId || state.route !== 'chats'
    },

    goBack () {
      for (let i = backHandlers.length - 1; i >= 0; i--) {
        if (backHandlers[i]()) return true
      }
      if (state.storyViewer) { store.set({ storyViewer: null }); return true }
      if (state.lightbox) { store.closeLightbox(); return true }
      if (state.dialog) { store.hideDialog(); return true }
      // Back shrinks a call rather than ending it. Hanging up is a red button
      // you mean to press; a gesture made to get back to the chat list used to
      // end the conversation instead. Once the call is a card, back goes on to
      // navigate the app as usual.
      if (state.activeCall && !state.callMinimized) { store.minimizeCall(); return true }
      if (state.panel) { store.set({ panel: false, mobileView: 'chat' }); return true }
      if (state.roomId) { store.closeRoom(); return true }
      if (state.route !== 'chats') { store.set({ route: 'chats' }); return true }
      return false
    },

    openLightbox (item) { store.set({ lightbox: item }) },
    /*
     * Deliberately does not revoke the URL it was showing.
     *
     * That URL belongs to the attachment cache in screens/message.js and is
     * the same one every thumbnail of the photo is rendering. Revoking it here
     * killed all of them: the images stayed on screen until the next re-mount
     * and then came back as their own file names, which is what leaving a chat
     * and returning to it did.
     */
    closeLightbox () { store.set({ lightbox: null }) }
  }

  /* --- wire client events into the store --- */

  client.on('status', (status, mode) => {
    store.set({ status, mode, peers: client.transport.peerCount })
  })
  client.on('rooms-changed', () => store.emit())
  client.on('room-update', () => {
    store.set({ peers: client.transport.peerCount })
    store.emit()
  })
  client.on('requests-changed', () => store.emit())
  client.on('request', () => store.emit())
  client.on('profile', () => store.emit())
  client.on('settings', (settings) => {
    applyTheme(settings.theme)
    setLanguage(settings.language || detectLanguage())
    store.emit()
  })

  if (calls) {
    /*
     * The ringing belongs to the platform, not to the page.
     *
     * Inside the app the banner is enough. Outside it — screen off, another
     * app in front — nobody is looking at a banner, and a call that only
     * exists in the page is a call nobody knows about. The Android shell puts
     * it on the lock screen with the device's ringtone; the web build has no
     * such hook and simply does nothing here.
     */
    const ring = (call) => {
      if (!platform.ringCall) return
      if (!call) {
        if (platform.stopRinging) platform.stopRinging()
        return
      }
      const summary = client.getSummary(call.roomId) || {}
      platform.ringCall({
        title: summary.name || t('call.incoming'),
        roomId: call.roomId || '',
        video: !!call.video
      })
    }

    /*
     * Routing is per call, not per screen.
     *
     * It is started here rather than in the call overlay because the overlay
     * is a component: it unmounts when the app is backgrounded or the window
     * is closed to the tray, and a call left in communication mode with
     * nothing to end it hands the volume keys to a call stream that no longer
     * exists. A video call starts on the loudspeaker, an audio one on the
     * earpiece — the button in the overlay moves it from there.
     */
    const audio = platform.callAudio

    /*
     * The call, as the phone's own call system sees it.
     *
     * On Android the shell reports each call to Telecom (see CallTelecom.kt),
     * which is what makes the system treat it as a call rather than as a
     * notification. Telecom has to be told when the call goes live and when
     * it ends — a connection nobody ends goes on ringing in its eyes — and
     * it can only be told from here, where both are known.
     */
    const telecom = platform.telecom && platform.telecom.available ? platform.telecom : null
    const endCause = (call, result) => {
      if (result === CALL_STATE.DECLINED) return call.initiator ? 'busy' : 'rejected'
      if (result === CALL_STATE.MISSED) return call.initiator ? 'local' : 'missed'
      return 'local'
    }

    /*
     * Each call, step by step, into the background journal the Android
     * shell keeps: how long ringing, answering and connecting took, and
     * which way the media went. "The call took ages to connect" cannot be
     * answered from anywhere else. Times only, and short ids.
     */
    const journal = platform.journal || null
    const trace = (call, how) => {
      if (!journal || !call || call._traced) return
      call._traced = true
      const started = Date.now()
      const tag = 'call ' + String(call.id || '').slice(0, 6)
      const at = () => '+' + ((Date.now() - started) / 1000).toFixed(1) + 's'
      const line = (text) => { try { journal(tag + ' ' + text) } catch {} }
      line(how + (call.video ? ', video' : '') + (call.group ? ', group' : ''))
      call.on('state', (state) => line(at() + ' ' + state))
      call.on('peer-state', (account, state) => line(at() + ' peer ' + String(account).slice(0, 6) + ' ' + state))
      call.on('path', (account, path) => line('path ' + path.local + '/' + path.remote + ' ' + path.protocol +
        (path.rtt != null ? ', rtt ' + path.rtt + ' ms' : '')))
      call.on('unreachable', () => line(at() + ' could not connect, giving up'))
      call.on('ended', ({ result, duration }) => line(at() + ' ended ' + result + ' after ' + duration + ' s'))
    }

    calls.on('incoming', (call) => {
      trace(call, 'ringing in')
      ring(call)
      store.set({ incomingCall: call })

      // Declined here, or the caller gave up: the call never becomes the
      // active one below, so this is the only place that sees it end.
      if (call && telecom) {
        call.on('ended', ({ result }) => {
          if (!call.joined) telecom.ended(result === CALL_STATE.DECLINED ? 'rejected' : 'missed')
        })
      }
    })
    calls.on('call', (call) => {
      trace(call, call && call.initiator ? 'calling out' : 'joined')
      // Answered, or ended: either way the phone stops ringing.
      ring(null)

      /*
       * Call progress, as a tone.
       *
       * This lives here and not in the call surface because the surface is a
       * component: it unmounts the instant a call ends, which is the exact
       * moment the "they hung up" tone has to play. Bound per call, and the
       * call object is thrown away when it finishes, so nothing accumulates.
       */
      if (call) {
        if (telecom) {
          const summary = client.getSummary(call.roomId) || {}
          if (call.initiator) {
            telecom.placed({
              title: summary.name || '',
              roomId: call.roomId || '',
              video: !!call.video
            })
          } else {
            // Answered on this phone.
            telecom.active()
          }
          call.on('state', (state) => { if (state === 'active') telecom.active() })
          call.on('ended', ({ result }) => telecom.ended(endCause(call, result)))
        }

        if (call.state === 'dialling') tones.ringback()
        call.on('state', (state) => { if (state !== 'dialling') tones.stop() })
        call.on('ended', ({ result, duration }) => {
          tones.stop()
          // Only for a call that never connected. A conversation that simply
          // finished does not need to be announced — both people were there.
          if (!duration && (result === CALL_STATE.DECLINED || result === CALL_STATE.MISSED)) {
            tones.busy()
          }
        })
      } else {
        tones.stop()
      }
      if (audio && audio.available) {
        if (call) audio.start(!!call.video)
        else audio.stop()
      }
      store.set({
        activeCall: call,
        speaker: !!(call && call.video),
        callMinimized: false,
        audioRoutes: call && audio && audio.available && audio.routes ? audio.routes() : null
      })
    })
  }

  /*
   * A headset connected mid-call moves the sound on its own, and the button
   * in the call bar has to follow without anybody asking it to.
   */
  // Frozen or thawed by the operator: the notice on the chat list follows.
  client.on('frozen', () => store.emit())

  if (typeof window !== 'undefined') {
    window.addEventListener('pine:unlocked', () => {
      if (state.activeCall) store.set({ callMinimized: true })
    })
    window.addEventListener('pine:audio-routes', (event) => {
      const routes = event.detail || null
      store.set({ audioRoutes: routes, speaker: !!(routes && routes.current === 'speaker') })
    })
  }

  applyTheme(client.settings.theme)
  setLanguage(client.settings.language || detectLanguage())

  return store
}

/** Subscribe a component to the whole store. */
export function useStore () {
  const store = useApp()
  const [, bump] = useReducer((n) => n + 1, 0)
  useEffect(() => store.subscribe(() => bump()), [store])
  return store
}

/** Open a Room and re-render whenever its view changes. */
export function useRoom (roomId) {
  const store = useApp()
  const [room, setRoom] = useState(null)
  const [, bump] = useReducer((n) => n + 1, 0)
  const current = useRef(null)

  useEffect(() => {
    let alive = true
    const known = roomId ? store.client.getSummary(roomId) : null
    if (!roomId || (known && known.placeholder)) {
      setRoom(null)
      return
    }
    store.client.openRoom(roomId).then((r) => {
      if (!alive) return
      current.current = r
      setRoom(r)
    }).catch(() => {})
    return () => { alive = false }
  }, [store, roomId])

  useEffect(() => {
    if (!room) return
    const offs = [
      room.on('update', () => bump()),
      room.on('typing', () => bump()),
      room.on('presence', () => bump()),
      room.on('meta', () => bump())
    ]
    return () => offs.forEach((off) => off())
  }, [room])

  return room
}

export function applyTheme (theme) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (theme === 'light' || theme === 'dark') root.setAttribute('data-theme', theme)
  else root.removeAttribute('data-theme')

  // Keep the mobile browser chrome in step with the app background.
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    const dark = theme === 'dark' ||
      (theme !== 'light' && matchMedia('(prefers-color-scheme: dark)').matches)
    meta.setAttribute('content', dark ? '#0c0f0e' : '#fbfbfa')
  }
}
